import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Tree } from 'primereact/tree';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { Toast } from 'primereact/toast';
import { FiEdit3, FiPlay } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import studentApi from './StudentApi';

const ExamTopicTree = ({ studentId, loadTreeTrigger, onSelectionChange, onPracticeClick, onExamsLoaded, voucher }) => {
  const [treeData, setTreeData] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [selection, setSelection] = useState({ examId: '', subjectId: '', topicId: '' });
  const [loading, setLoading] = useState(false);
  const toastRef = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ✅ useCallback ensures stable function reference for useEffect
  // const fetchData = useCallback(async () => {
  //   try {
  //     // Fetch exams, subjects, topics, and vouchers in parallel
  //     const [examRes, subjectRes, topicRes, voucherRes] = await Promise.all([
  //       studentApi.get('/exams'),
  //       studentApi.get('/subjects'),
  //       studentApi.get('/topics'),
  //       studentApi.get(`/student-vouchers/${studentId}`)
  //     ]);

  //     let exams = examRes.data || [];
  //     let subjects = subjectRes.data || [];
  //     let topics = topicRes.data || [];
  //     let vouchers = voucherRes.data?.data || [];

  //     // ✅ Only active/running vouchers
  //     let activeVouchers = vouchers.filter(v => v.status === 'running');

  //     // ✅ If a voucher is selected from "Set Exam", restrict data
  //     if (voucher) {
  //       activeVouchers = activeVouchers.filter(v => v.id === voucher.id);

  //       if (voucher.exam_id) {
  //         exams = exams.filter(ex => Number(ex.id) === Number(voucher.exam_id));
  //       }
  //       if (voucher.subject_id) {
  //         subjects = subjects.filter(s => Number(s.id) === Number(voucher.subject_id));
  //       }
  //       if (voucher.topic_id) {
  //         topics = topics.filter(t => Number(t.id) === Number(voucher.topic_id));
  //       }
  //     }

  //     // ✅ Build tree structure here with filtered exams/subjects/topics/activeVouchers
  //     const treeData = exams.map(exam => {
  //       const examSubjects = subjects.filter(s => s.exam_id === exam.id);
  //       const subjectNodes = examSubjects.map(subject => {
  //         const subjectTopics = topics.filter(t => t.subject_id === subject.id);
  //         const topicNodes = subjectTopics.map(topic => ({
  //           id: `topic-${topic.id}`,
  //           label: topic.title,
  //           type: 'topic',
  //           data: topic,
  //         }));

  //         return {
  //           id: `subject-${subject.id}`,
  //           label: subject.name,
  //           type: 'subject',
  //           data: subject,
  //           children: topicNodes,
  //         };
  //       });

  //       return {
  //         id: `exam-${exam.id}`,
  //         label: exam.title,
  //         type: 'exam',
  //         data: exam,
  //         children: subjectNodes,
  //       };
  //     });

  //     setTree(treeData);

  //     if (onExamsLoaded) {
  //       onExamsLoaded(treeData);
  //     }
  //   } catch (err) {
  //     console.error("Error fetching data for ExamTopicTree:", err);
  //     setTree([]);
  //   }
  // }, [studentId, onExamsLoaded, voucher]);

  // inside ExamTopicTree component
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [examRes, subjectRes, topicRes, voucherRes] = await Promise.all([
        studentApi.get('/exams'),
        studentApi.get('/subjects'),
        studentApi.get('/topics'),
        studentApi.get(`/student-vouchers/${studentId}`)
      ]);

      let exams = examRes.data || [];
      let subjects = subjectRes.data || [];
      let topics = topicRes.data || [];
      let vouchers = voucherRes.data?.data || [];

      // accept common active states (be tolerant)
      let activeVouchers = vouchers.filter(v => ['active', 'running'].includes(String(v.status)));

      // If parent passed a voucher to "set exam", restrict results accordingly
      if (voucher) {
        // restrict to that voucher if present in voucher list
        activeVouchers = activeVouchers.filter(v => Number(v.id) === Number(voucher.id));

        if (voucher.exam_id) {
          exams = exams.filter(ex => Number(ex.id) === Number(voucher.exam_id));
        }
        if (voucher.subject_id) {
          subjects = subjects.filter(s => Number(s.id) === Number(voucher.subject_id));
        }
        if (voucher.topic_id) {
          topics = topics.filter(t => Number(t.id) === Number(voucher.topic_id));
        }
      }

      // 🔴 Debug logs here
      console.log("Voucher passed into fetchData:", voucher);
      console.log("Exams from API (after filtering):", exams);
      console.log("Subjects from API (after filtering):", subjects);
      console.log("Topics from API (after filtering):", topics);


      // Build nodes using 'key' (PrimeReact Tree expects unique 'key')
      const builtTree = exams.map(exam => {
        const examSubjects = subjects.filter(s => Number(s.exam_id) === Number(exam.id));
        const subjectNodes = examSubjects.map(subject => {
          const subjectTopics = topics.filter(t => Number(t.subject_id) === Number(subject.id));
          const topicNodes = subjectTopics.map(topic => ({
            key: `topic-${exam.id}-${subject.id}-${topic.id}`,
            label: topic.title,
            type: 'topic',
            data: topic,
          }));

          return {
            key: `subject-${exam.id}-${subject.id}`,
            label: subject.name,
            type: 'subject',
            data: subject,
            children: topicNodes,
          };
        });

        return {
          key: `exam-${exam.id}`,
          label: exam.title,
          type: 'exam',
          data: exam,
          children: subjectNodes,
        };
      });

      // set the state the Tree uses
      setTreeData(builtTree);

      // let parent know how many exams we have (pass length, not array)
      if (onExamsLoaded) onExamsLoaded(builtTree.length);
    } catch (err) {
      console.error('Error fetching data for ExamTopicTree:', err);
      setTreeData([]);
      if (onExamsLoaded) onExamsLoaded(0);
    } finally {
      setLoading(false);
    }
  }, [studentId, onExamsLoaded, voucher]);


  useEffect(() => {
  if (voucher) {
    fetchData(); // always fetch when voucher is present
  } else {
    setTreeData([]); // clear tree if no voucher yet
  }
}, [loadTreeTrigger, studentId, fetchData, voucher]);



  const getNamesFromIds = (examId, subjectId, topicId) => {
    const examNode = treeData.find(e => e.key === `exam-${examId}`);
    if (!examNode) return {};
    const examName = examNode.label;
    let subjectName = '', topicName = '';
    if (subjectId) {
      const subjectNode = examNode.children?.find(s => s.key === `subject-${examId}-${subjectId}`);
      subjectName = subjectNode?.label || '';
      if (topicId) {
        const topicNode = subjectNode?.children?.find(t => t.key === `topic-${examId}-${subjectId}-${topicId}`);
        topicName = topicNode?.label || '';
      }
    }
    return { examName, subjectName, topicName };
  };

  const extractIdsFromKey = (key) => {
    const parts = key.split('-');
    if (parts[0] === 'exam' && parts.length === 2) return { examId: parts[1] };
    if (parts[0] === 'subject' && parts.length === 3) return { examId: parts[1], subjectId: parts[2] };
    if (parts[0] === 'topic' && parts.length === 4) return { examId: parts[1], subjectId: parts[2], topicId: parts[3] };
    return {};
  };


  const handleSelection = (e) => {
    const fullKey = e.value;
    if (!fullKey) return;
    const parts = fullKey.split('-');
    let updated = { examId: '', subjectId: '', topicId: '' };
    if (parts[0] === 'exam' && parts.length === 2) updated.examId = parts[1];
    else if (parts[0] === 'subject' && parts.length === 3) { updated.examId = parts[1]; updated.subjectId = parts[2]; }
    else if (parts[0] === 'topic' && parts.length === 4) { updated.examId = parts[1]; updated.subjectId = parts[2]; updated.topicId = parts[3]; }

    setSelection(updated);
    setSelectedKey(fullKey);
    if (onSelectionChange) onSelectionChange(updated);

    if (parts[0] === 'topic' && toastRef.current) {
      const { examName, subjectName, topicName } = getNamesFromIds(updated.examId, updated.subjectId, updated.topicId);
      toastRef.current.show({
        severity: 'success',
        summary: 'Topic Selected',
        detail: `Exam ${examName} → Subject ${subjectName} → Topic ${topicName}`,
        life: 3000,
      });
    }
  };

  const handleNodeClick = (e) => {
    const key = e.node.key;
    setExpandedKeys(prev => ({ ...prev, [key]: prev[key] ? undefined : true }));
  };

  const getNodeStyle = (level) => {
    // const baseColor = '#0d47a1';
    const baseColor ='#638fd6'
   // const shade = { exam: '#64b5f6', subject: '#bbdefb', topic: '#e3f2fd' };
   const shade = { exam: '#bbdefb', subject: '#e3f2fd', topic: '#f1f8ff' };
    let style = { padding: '6px 12px', borderRadius: '6px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' };
    if (level === 2) style = { ...style, backgroundColor: shade.exam,   color: '#0d47a1', fontWeight: 'bold', fontSize: '1rem' };
    else if (level === 3) style = { ...style, backgroundColor: shade.subject, color: baseColor, fontWeight: 600 };
    else if (level === 4) style = { ...style, backgroundColor: shade.topic, color: baseColor };
    return style;
  };



  const nodeTemplate = (node) => {
    const level = (node?.key?.split('-').length) || 0;
    const iconClass = level === 2 ? 'pi pi-folder' : level === 3 ? 'pi pi-book' : level === 4 ? 'pi pi-file' : 'pi pi-question';

    return (
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" sx={{ ...getNodeStyle(level), flexWrap: 'wrap' }}>
        <Box display="flex" alignItems="center" flexShrink={1}>
          <i className={iconClass} style={{ marginRight: 6 }} />
          <Typography variant="body2">{node?.label || ''}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginLeft: 2, marginTop: { xs: 1, sm: 0 }, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
          {isMobile ? (
            <>
              <IconButton
                color="secondary"
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  const ids = extractIdsFromKey(node.key);
                  if (!ids.examId || !ids.topicId) return;
                  navigate('/practice', {
                    state: {
                      examId: ids.examId,
                      subjectId: ids.subjectId,
                      topicId: ids.topicId,
                      voucherId: voucher.id,
                      studentId,
                    },
                  });
                }}
              >
                <FiEdit3 />
              </IconButton>


              <IconButton color="primary" size="small" onClick={e => { e.stopPropagation(); const { examId, topicId } = extractIdsFromKey(node.key); if (!examId || !topicId) return; navigate('/test', { state: { examId, topicId } }); }}>
                <FiPlay />
              </IconButton>
            </>
          ) : (
            <>
              {/* <Button variant="outlined" 
              color="secondary" 
              size="small" 
              startIcon={<FiEdit3 />}
               onClick={e => { e.stopPropagation(); const ids = extractIdsFromKey(node.key); if (!ids.examId) 
               return; navigate('/practice', { state: ids }); }} 
               sx={{ borderRadius: '999px',
                textTransform: 'none', fontSize: '0.75rem' }}>Practice
                </Button> */}


              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<FiEdit3 />}
                onClick={e => {
                  e.stopPropagation();
                  const ids = extractIdsFromKey(node.key);
                  if (!ids.examId) return;

                  // ✅ include voucherId and studentId when navigating
                  navigate('/practice', {
                    state: {
                      examId: ids.examId,
                      subjectId: ids.subjectId,
                      topicId: ids.topicId,
                      voucherId: voucher?.id || null,
                      studentId,                        // ✅ optional, if you track student
                    },
                  });
                }}
                sx={{ borderRadius: '999px', textTransform: 'none', fontSize: '0.75rem' }}
              >
                Practice
              </Button>




              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<FiPlay />}
                onClick={e => {
                  e.stopPropagation();
                  if (!onPracticeClick) return;

                  const ids = extractIdsFromKey(node.key);

                  // 🟢 Pass along voucher info to ensure correct filtering later
                  onPracticeClick({
                    examId: ids.examId,
                    subjectId: ids.subjectId,
                    topicId: ids.topicId,
                    voucherId: voucher?.id || null,
                  });
                }}
              >
                Test
              </Button>

            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '700px', mx: 'auto', px: { xs: 1, sm: 2 }, py: { xs: 2, sm: 3 } }}>
      <Toast ref={toastRef} position="bottom-right" />
      <Typography variant="subtitle1" gutterBottom>📚 Select Class → Subject → Topic</Typography>
      <Box mt={1}>
        <Tree
          value={treeData}
          selectionMode="single"
          selectionKeys={selectedKey}
          onSelectionChange={handleSelection}
          expandedKeys={expandedKeys}
          onToggle={e => setExpandedKeys(e.value)}
          onNodeClick={handleNodeClick}
          nodeTemplate={nodeTemplate}
          className="clean-tree"
          loading={loading}
        />
      </Box>
      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          Selected: {selection.examId}{selection.subjectId && ` → ${selection.subjectId}`}{selection.topicId && ` → ${selection.topicId}`}
        </Typography>
      </Box>
    </Box>
  );
};

export default ExamTopicTree;
